import { Registry, Storage } from '@lightningjs/sdk'
import QRCode from 'qrcode'

import { INACTIVITY_THRESHOLD } from '../constants/general'
import { AppConfig } from '../services/appConfig'

export const objectFromEntries = (entries) => {
  if (!entries || !entries[Symbol.iterator]) return ''

  const obj = {}

  for (const [key, value] of entries) {
    obj[key] = value
  }

  return obj
}

export const isObjEmpty = (obj) => {
  return Object.keys(obj).length === 0
}

export const areObjsEqual = (previous, latest) => {
  return JSON.stringify(previous) === JSON.stringify(latest)
}

export const joinStrings = (separator, ...strings) => {
  return strings.filter((str) => str !== undefined && str !== '').join(` ${separator} `)
}

export const chunkArray = (array, size) => {
  const chunks = Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
    array.slice(i * size, i * size + size),
  )

  return chunks
}

export const generateQR = (text: string, opts?: object): Promise<string> => {
  return QRCode.toDataURL(text, {
    margin: 0,
    color: { dark: '#ffffffff', light: '#00000000' },
    width: 500,
    ...opts,
  })
    .then((QR) => QR)
    .catch((err) => console.error(err))
}

export const hasBeenInactive = () => {
  const timeSinceLastActivity = Date.now() - AppConfig.shared.appSettings.getInactivityTimestamp()
  return timeSinceLastActivity > INACTIVITY_THRESHOLD
}

export const delayCall = (): Promise<void> => {
  return new Promise((resolve) => Registry.setTimeout(() => resolve(), 0))
}

export function createInput({ text, type, onComplete, onInput }) {
  const input = document.createElement('input')
  input.type = type || 'text'
  input.style.position = 'absolute'
  input.style.opacity = '0'
  input.value = text
  document.body.appendChild(input)

  let isClosing = false

  const closeInput = () => {
    if (isClosing) return
    isClosing = true

    // Make sure we do this on the next frame because _handleEnter on
    // Lightning component was being called right after we removed the
    // element and was creating a new element which was causing trouble
    setTimeout(() => {
      onComplete(input.value)
      document.body.removeChild(input)
    }, 0)
  }

  // Listen for each character typed
  input.addEventListener('input', () => {
    if (onInput) {
      onInput(input.value)
    }
  })

  input.addEventListener('keydown', (ev) => {
    const code = ev.keyCode
    if (code === 13 || code === 65376 || code === 27 || code === 65385) {
      closeInput()
    }
  })

  input.onblur = () => {
    // On LG and PC, clicking on the input field on the canvas makes the real input blur.
    // This timeout forces the focus. Focusing the canvas input with the keyboard never
    // triggers the onblur method, so for platforms like Vizio, Samsung or PC while using
    // the keyboard this method would never execute
    setTimeout(() => {
      input?.focus()
    })
  }

  input.onfocus = () => {
    // Solves an issue where, if navigating away by pressing down from the field and then returning
    // to it by pressing up would cause the caret to go to the beginning of the input field
    setTimeout(() => {
      const value = input.value
      input.value = ''
      input.value = value
    }, 0)
  }

  input.focus()

  return closeInput
}

export function validateEmail(email: string): boolean {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return pattern.test(email?.toLowerCase())
}

export const debounce = (callback, wait) => {
  let timerId = null

  const debouncedFunction = (...args) => {
    if (timerId) {
      Registry.clearTimeout(timerId)
    }

    timerId = Registry.setTimeout(() => {
      callback(...args)
      timerId = null
    }, wait)
  }

  debouncedFunction.cancel = () => {
    if (timerId) {
      Registry.clearTimeout(timerId)
      timerId = null
    }
  }

  return debouncedFunction
}

export const checkLocalFirstUseFlag = () => {
  return !!Storage.get('isFirstUse')
}

export const createLocalFirstUseFlag = () => {
  Storage.set('isFirstUse', true)
}

export const replaceQueryParam = (url, param, newValue) => {
  const [path, queryString] = url.split('?')
  const searchParams = new URLSearchParams(queryString || '')

  searchParams.set(param, newValue)

  const newQueryString = searchParams.toString()
  return newQueryString ? `${path}?${newQueryString}` : path
}

/**
 * Performs a deep copy of one object to another but doesn't override any properties
 * that already exist.
 * @param src The object to copy
 * @param dest The destination object
 * @param force Forces a property to be overridden even if it exists
 * @param protectedProps Protects some properties from being overridden even if force is true
 */
export function deepCopyProperties(
  src: any,
  dest: any,
  force = false,
  protectedProps: string[] = [],
) {
  Object.keys(src).forEach((key) => {
    if (protectedProps.includes(key)) {
      return
    }

    if (
      typeof src[key] === 'object' &&
      ((src[key] !== null && isEmptyOrNull(dest[key])) || force)
    ) {
      if (src[key] instanceof Date) {
        dest[key] = new Date(src[key].getTime())
      } else {
        dest[key] ??= Array.isArray(src[key]) ? [] : {}
      }
    }
    if (typeof src[key] === 'object' && src[key] !== null && !(src[key] instanceof Date)) {
      deepCopyProperties(src[key], dest[key], force, protectedProps)
    } else if (isEmptyOrNull(dest[key]) || force) {
      dest[key] = src[key]
    }
  })
}

/**
 * Check if an object is undefined, null or empty string.
 * @param obj The object to be checked
 * @returns True if object is undefined or null or empty string, false otherwise.
 */
function isEmptyOrNull(obj: any) {
  return obj === undefined || obj === null || obj === ''
}

/**
 * Checks if the provided parameter is an instance of ErrorType.
 *
 * @param obj The object to check.
 * @returns `true` if the object has an 'error' property, narrowing the type to ErrorType.
 */
export function isError(obj: unknown): obj is ErrorType {
  return 'error' in (obj as ErrorType)
}

type RequestOptions = {
  /** Prevents the request from being called if one is already in progress. */
  preventConcurrent?: boolean
}

/**
 * Wraps an asynchronous request function to provide additional featuers
 * like concurrency control and cancellation.
 *
 * @template F The type of the request function.
 * @param request The asynchronous function to wrap.
 * @param options Configuration options:
 *   - preventConcurrent: If true, prevents the request from being called if one is already in progress.
 * @returns An object with:
 *   - fetch: A function to execute the request, respecting concurrency and cancellation.
 *   - cancel: A function to cancel the current request.
 *
 * Example usage:
 *   ```
 *   const wrapped = createRequest(fetchData, { preventConcurrent: true });
 *   wrapped.fetch(args);
 *   wrapped.cancel();
 *   ```
 */
export function createRequest<F extends (...args: any[]) => any>(
  request: F,
  { preventConcurrent }: RequestOptions,
) {
  let cancel = false
  let fetching = false

  return {
    cancel: () => {
      cancel = true
    },
    fetch: async (...args: Parameters<F>): Promise<Awaited<ReturnType<F>> | undefined> => {
      try {
        if (preventConcurrent && fetching) {
          return undefined
        }

        fetching = true

        const response = await request(...args)

        fetching = false

        return !cancel ? response : undefined
      } finally {
        fetching = false
      }
    },
  }
}

export type WrappedRequest<F extends (...args: any[]) => any> = ReturnType<
  typeof createRequest<F> | undefined
>
